import KeenSlider from 'keen-slider';
import Sortable from 'sortablejs';
class Attachment {
  constructor(
    slideOptions = { spacing: 8 },
    elem = null,
    isComment = false,
    isSort = false,
    maxFileNum = 3,
    excludeType = [],
  ) {
    this.slideOptions = slideOptions;
    this.isComment = isComment;
    this.maxFileNum = maxFileNum;
    this.isSort = isSort;
    this.imageTypes = ['image/gif', 'image/jpeg', 'image/png'];
    this.excludeType = excludeType;
    this.$attachment = elem ?? document.querySelector('.js-attachment');
    if (!this.$attachment) return;

    this.$finderButton = this.$attachment.querySelector(
      this.$attachment.dataset.attachmentFinderButton,
    );

    this.$cacheField = this.$attachment.querySelector(
      this.$attachment.dataset.attachmentCache,
    );

    this.$fileField = this.$attachment.querySelector(
      this.$attachment.dataset.attachmentFieldAdd,
    );

    this.$deleteField = this.$attachment.querySelector(
      this.$attachment.dataset.attachmentFieldDelete,
    );

    this.$imageList = this.$attachment.querySelector(
      this.$attachment.dataset.attachmentImageList,
    );

    this.$fileList = this.$attachment.querySelector(
      this.$attachment.dataset.attachmentFileList,
    );

    this.$fileExisting = this.$attachment.querySelectorAll(
      this.$attachment.dataset.attachmentFileExisting,
    );

    this.$svg = {
      image: this.createSVGRender(
        'image',
        this.$attachment.dataset.attachmentImageCloseIconUrl,
      ),
      file: this.createSVGRender(
        'file',
        this.$attachment.dataset.attachmentFileCloseIconUrl,
      ),
      doc: this.createSVGRender(
        'doc',
        this.$attachment.dataset.attachmentFileIconUrl,
      ),
    };

    this.$finderButton.addEventListener(
      'click',
      this.handleOpenFinder.bind(this),
    );
    this.$fileField.addEventListener('change', this.handleChange.bind(this));

    this.$fileExisting.forEach((elem) => {
      elem.addEventListener('click', this.handleDeleteExisting.bind(this));
    });

    // SNSでのみ利用
    if (this.isSort) {
      this.$fileSortField = this.$attachment.querySelector(
        this.$attachment.dataset.attachmentFileSortField,
      );
      this.setSortable();
    } else {
      this.setKeenSlider();
    }
  }

  getExistingFiles() {
    const images = this.$imageList.children;
    const files = this.$fileList.children;
    return [...images].concat([...files]);
  }

  selectedFiles() {
    return this.files ? [...this.files] : [];
  }

  fileSortByType(files) {
    const sorted = { image: [], file: [] };
    [...files].forEach((file) => {
      const type = `${this.imageTypes.includes(file.type) ? 'image' : 'file'}`;
      sorted[type].push(file);
    });
    return sorted;
  }

  handleOpenFinder() {
    this.$fileField.dispatchEvent(new MouseEvent('click'));
  }

  handleChange({ target: { files } }) {
    if (files.length < 1) {
      this.setFiles();
      return;
    }

    const changedFiles = this.fileSortByType(files);
    const existingFiles = this.getExistingFiles();

    // 画像のみ許可の投稿の場合（現状はSNSのみ）
    if (
      this.excludeType.includes('image') &&
      changedFiles.image.length + existingFiles.length > this.maxFileNum
    ) {
      alert(`アップロード可能な画像は${this.maxFileNum}ファイルまでです`);
      // input[type="file"]のリセット
      this.setFiles();
      return;
    }
    // 画像・ファイル混合の投稿の場合
    else if (
      changedFiles.image.length +
        changedFiles.file.length +
        existingFiles.length >
      this.maxFileNum
    ) {
      alert(`アップロード可能なファイルは${this.maxFileNum}ファイルまでです`);
      // input[type="file"]のリセット
      this.setFiles();
      return;
    }

    if (![...files].every((file) => file.size <= 10 * 1024 * 1024)) {
      if (this.excludeType.includes('file')) {
        alert('画像ファイルは10MB以下にしてください');
      } else {
        alert('添付ファイルは10MB以下にしてください');
      }
      // input[type="file"]のリセット
      this.setFiles();
      return;
    }

    this.setFiles(files);
  }

  // 新たに追加されるケースのみ
  handleDelete(type, fileName) {
    if (this.files?.length < 1) return;

    const list = this[`$${type}List`];
    const dataTransfer = new DataTransfer();

    // Attachmentの削除、親の非表示
    list.querySelector(`[data-attachment-name="${fileName}"]`)?.remove();
    if (list.childElementCount < 1) {
      list.ariaHidden = true;
    }

    this.selectedFiles().forEach((file) => {
      file.name !== fileName && dataTransfer.items.add(file);
    });
    this.$fileField.files = dataTransfer.files;
    this.files = dataTransfer.files;
    if (this.isSort) {
      this.updateSortFieldValues();
    }
  }

  // 既存のファイルのみ
  handleDeleteExisting({ target }) {
    const item = target.parentElement;
    const list = item.parentElement;
    const cache = this.$cacheField.value.split(' ');
    const id =
      target.dataset.attachmentDeleteId ||
      target.dataset.attachmentDeleteCacheId;

    if (target.dataset.attachmentDeleteId) {
      this.$deleteField.insertAdjacentElement(
        'afterend',
        this.createDeleteField(id),
      );
    } else {
      this.$cacheField.value = cache.filter((v) => v !== id).join(' ');
    }

    item.remove();

    if (list.childElementCount < 1) {
      list.ariaHidden = true;
    }

    // FIXME: jQuery
    const type = $(item).prop('class').includes('c-Attachment_ImageItem')
      ? 'image'
      : 'file';
    const fileName = $(item).data('attachmentName');
    this.handleDelete(type, fileName);
  }

  createDeleteField(id) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = this.$deleteField.dataset.attachmentDeleteName.replace(
      'ID',
      id,
    );
    return input;
  }

  async createSVGRender(key, url) {
    if (this.$svg?.[key] === undefined) {
      const response = await fetch(url);
      const text = await response.text();
      const domParser = new DOMParser();
      const svg = domParser.parseFromString(
        text,
        'image/svg+xml',
      ).documentElement;
      const className = key === 'doc' ? 'c-Attachment_Icon' : 'c-Attachment_X';
      svg.classList.add(className);
      this.$svg[key] = svg;
    }
    return this.$svg[key];
  }

  async createItemRender(reader, file, type) {
    const {
      0: filename,
      length,
      [length - 1]: extension,
    } = file.name.split('.');
    const renderSet = {
      li: {
        element: 'li',
        class:
          type === 'image'
            ? this.isSort
              ? ['c-Attachment_ImageItem', 'sort']
              : ['c-Attachment_ImageItem']
            : ['c-Attachment_FileItem'],
        data: {
          attachmentName: `${file.name}`,
        },
      },
      button: {
        element: 'button',
        class: ['c-Attachment_Remove'],
        type: 'button',
      },
      span: {
        element: 'span',
        class: ['c-Attachment_Filename', 'text-gray-900'],
        data: {
          attachmentType: type,
        },
      },
      name: {
        element: 'span',
        class: ['c-Attachment_Name'],
        text: filename,
      },
      ext: {
        element: 'span',
        class: ['c-Attachment_Ext'],
        text: `.${extension}`,
      },
    };

    if (type === 'image') {
      Object.assign(renderSet, {
        figure: {
          element: 'figure',
          class: ['c-Attachment_Fig'],
        },
        img: {
          element: 'img',
          class: ['c-Attachment_Image'],
          src: reader.result,
          alt: '',
        },
      });
    }

    const [li, button, span, name, ext, figure, img] = Object.keys(
      renderSet,
    ).map((key) => {
      const element = document.createElement(renderSet[key].element);

      if (renderSet[key].class) {
        element.classList.add(...renderSet[key].class);
      }

      if (renderSet[key].text) {
        element.textContent = renderSet[key].text;
      }

      if (renderSet[key].type) {
        element.type = renderSet[key].type;
      }

      if (renderSet[key].src) {
        element.src = renderSet[key].src;
      }

      if (renderSet[key].alt) {
        element.alt = renderSet[key].alt;
      }

      if (renderSet[key].style) {
        element.style.cssText = renderSet[key].style;
      }

      if (renderSet[key].data) {
        Object.keys(renderSet[key].data).forEach((dataKey) => {
          element.dataset[dataKey] = renderSet[key].data[dataKey];
        });
      }

      return element;
    });

    span.append(name, ext);
    button.append(this.$svg[type].cloneNode(true));
    button.addEventListener(
      'click',
      this.handleDelete.bind(this, type, file.name),
    );

    if (type === 'image') {
      figure.append(img);
      li.append(button, figure, span);
    } else {
      const doc = this.$svg.doc.cloneNode(true);
      li.append(doc, span, button);
    }

    return li;
  }

  setKeenSlider() {
    const MutationPlugin = (slider) => {
      const observer = new MutationObserver((mutations) =>
        mutations.forEach(() => slider.update()),
      );
      slider.on('created', () => {
        observer.observe(slider.container, { childList: true });
      });
      slider.on('destroyed', () => {
        observer.disconnect();
      });
    };

    const slides = {
      origin: 0,
      perView: 'auto',
    };

    Object.assign(slides, this.slideOptions);

    new KeenSlider(
      this.$attachment.querySelector(
        this.$attachment.dataset.attachmentImageList,
      ),
      {
        selector: this.$attachment.dataset.attachmentImageItem,
        disabled: true,
        breakpoints: {
          '(max-width: 768px)': {
            disabled: false,
            loop: false,
            slidesPerView: 3,
            mode: 'free-snap',
            slides,
          },
        },
      },
      [MutationPlugin],
    );
  }

  setSortable() {
    const fileSortField = this.$fileSortField;
    const sortable = document.querySelector('.sortable');
    if (!fileSortField || !sortable) return;

    new Sortable(sortable, {
      animation: 150,
      onEnd: this.updateSortFieldValues,
    });
  }

  updateSortFieldValues = () => {
    const items = {};
    const sortItems = document.querySelectorAll('.sort');
    sortItems.forEach((item, index) => {
      items[item.dataset.attachmentName] = index + 1;
    });
    this.$fileSortField.value = JSON.stringify(items);
  };

  setFiles(files = []) {
    // データの追加
    const uniqued = [...this.selectedFiles(), ...files].filter(
      (file, i, merged) => merged.findIndex((f) => f.name === file.name) === i,
    );

    const dataTransfer = new DataTransfer();
    uniqued.forEach((file) => {
      dataTransfer.items.add(file);
    });
    this.$fileField.files = dataTransfer.files;
    this.files = dataTransfer.files;

    // ファイルがない場合、またはJS側のValidateエラーの場合はプレビューの生成を行わない
    if (files.length < 1) return;

    // UIの追加
    [...this.files].forEach((file) => {
      const type = `${this.imageTypes.includes(file.type) ? 'image' : 'file'}`;

      // 追加するファイル分を全て一度消す
      document
        .querySelectorAll(`[data-attachment-name="${file.name}"]`)
        .forEach((elem) => {
          elem.remove();
        });

      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.addEventListener('load', async () => {
        await this.createItemRender(reader, file, type)
          .then((li) => {
            const list = this[`$${type}List`];
            // Attachmentの追加、親の表示
            list.appendChild(li);
            if (list.childElementCount > 0) {
              list.ariaHidden = false;
            }
          })
          .then(() => {
            if (this.isSort) {
              this.updateSortFieldValues();
            }
          });
      });
    });
  }
}

const AttachmentMutationObserve = (element) => {
  const selectorMutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      Array.from(mutation.addedNodes)
        .filter((node) => node.nodeType === Node.ELEMENT_NODE)
        .forEach((node) => {
          const targets = node.querySelectorAll('.js-attachment');
          Array.from(targets).forEach((target) => {
            target && new Attachment({ spacing: 12 }, target, true);
          });
        });
    });
  });
  selectorMutationObserver.observe(element, { childList: true, subtree: true });
};

export default Attachment;
export { AttachmentMutationObserve };
